/**
 * Statistics for reading an A/B experiment.
 *
 * Everything here works on raw counts. The results endpoint sends numerators
 * and denominators precisely so this file never has to reconstruct a count by
 * multiplying a rate back out by the audience size.
 */

/**
 * Normal CDF approximation (Abramowitz & Stegun)
 */
export function normalCDF (x) {
  const a1 = 0.254829592
  const a2 = -0.284496736
  const a3 = 1.421413741
  const a4 = -1.453152027
  const a5 = 1.061405429
  const p = 0.3275911
  const sign = x < 0 ? -1 : 1
  x = Math.abs(x) / Math.sqrt(2)
  const t = 1.0 / (1.0 + p * x)
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) *
    t + a1) * t * Math.exp(-x * x)
  return 0.5 * (1.0 + sign * y)
}

/**
 * Inverse normal CDF (Acklam's approximation). Used for the z multipliers
 * behind power and minimum-detectable-effect calculations.
 */
export function normalQuantile (p) {
  if (p <= 0) return -Infinity
  if (p >= 1) return Infinity

  const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687,
    138.3577518672690, -30.66479806614716, 2.506628277459239]
  const b = [-54.47609879822406, 161.5858368580409, -155.6989798598866,
    66.80131188771972, -13.28068155288572]
  const c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838,
    -2.549732539343734, 4.374664141464968, 2.938163982698783]
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996,
    3.754408661907416]

  const pLow = 0.02425
  const pHigh = 1 - pLow
  let q

  if (p < pLow) {
    q = Math.sqrt(-2 * Math.log(p))
    return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
  }
  if (p > pHigh) {
    q = Math.sqrt(-2 * Math.log(1 - p))
    return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
  }
  q = p - 0.5
  const r = q * q
  return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q /
    (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
}

/**
 * Wilson score 95% confidence interval for a proportion.
 * Returns [lower, upper].
 */
export function wilsonCI (successes, total) {
  if (!total || total <= 0) return [0, 0]
  const z = 1.96
  const p = successes / total
  const denom = 1 + z * z / total
  const center = p + z * z / (2 * total)
  const spread = z * Math.sqrt(
    (p * (1 - p) + z * z / (4 * total)) / total
  )
  return [
    Math.max(0, (center - spread) / denom),
    Math.min(1, (center + spread) / denom)
  ]
}

/**
 * Two-proportion z-test. Returns { zScore, pValue }.
 * Tests whether pB differs from pA.
 */
export function proportionZTest (
  successesA, totalA, successesB, totalB
) {
  if (!totalA || !totalB) {
    return { zScore: 0, pValue: 1 }
  }
  const pA = successesA / totalA
  const pB = successesB / totalB
  const pooled = (successesA + successesB) /
    (totalA + totalB)
  const se = Math.sqrt(
    pooled * (1 - pooled) * (1 / totalA + 1 / totalB)
  )
  if (se === 0) return { zScore: 0, pValue: 1 }
  const z = (pB - pA) / se
  const pValue = 2 * (1 - normalCDF(Math.abs(z)))
  return { zScore: z, pValue }
}

/**
 * Regularized upper incomplete gamma Q(s, x), by series expansion below the
 * transition point and continued fraction above it. Backs the chi-square
 * p-value used for the sample ratio mismatch check.
 */
function gammaQ (s, x) {
  if (x < 0 || s <= 0) return 1
  if (x === 0) return 1

  const logGamma = (z) => {
    // Lanczos approximation
    const g = 7
    const c = [0.99999999999980993, 676.5203681218851, -1259.1392167224028,
      771.32342877765313, -176.61502916214059, 12.507343278686905,
      -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7]
    if (z < 0.5) {
      return Math.log(Math.PI / Math.sin(Math.PI * z)) - logGamma(1 - z)
    }
    z -= 1
    let a = c[0]
    const t = z + g + 0.5
    for (let i = 1; i < g + 2; i++) a += c[i] / (z + i)
    return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(a)
  }

  const lnGammaS = logGamma(s)

  if (x < s + 1) {
    // Series for the lower incomplete gamma, then complement.
    let sum = 1 / s
    let term = sum
    for (let n = 1; n < 300; n++) {
      term *= x / (s + n)
      sum += term
      if (Math.abs(term) < Math.abs(sum) * 1e-15) break
    }
    const lower = sum * Math.exp(-x + s * Math.log(x) - lnGammaS)
    return Math.max(0, Math.min(1, 1 - lower))
  }

  // Continued fraction (Lentz) for the upper incomplete gamma.
  const tiny = 1e-300
  let b = x + 1 - s
  let c2 = 1 / tiny
  let d = 1 / b
  let h = d
  for (let i = 1; i < 300; i++) {
    const an = -i * (i - s)
    b += 2
    d = an * d + b
    if (Math.abs(d) < tiny) d = tiny
    c2 = b + an / c2
    if (Math.abs(c2) < tiny) c2 = tiny
    d = 1 / d
    const del = d * c2
    h *= del
    if (Math.abs(del - 1) < 1e-15) break
  }
  const upper = Math.exp(-x + s * Math.log(x) - lnGammaS) * h
  return Math.max(0, Math.min(1, upper))
}

/**
 * Chi-square survival function — the probability of a statistic at least this
 * extreme under the null.
 */
export function chiSquarePValue (chiSquare, df) {
  if (df <= 0 || !isFinite(chiSquare) || chiSquare <= 0) return 1
  return gammaQ(df / 2, chiSquare / 2)
}

/**
 * Sample ratio mismatch: does the observed split match the configured weights?
 *
 * A mismatch means randomisation or assignment is broken, and no downstream
 * metric can be trusted — so this is the first thing the page checks. Note the
 * expected split is the *current* configuration, and weights can be edited
 * while an experiment runs, so a mid-flight weight change shows up here too.
 */
export function sampleRatioMismatch (allocation) {
  const rows = (allocation || []).filter(a => a.expectedCount > 0)
  const totalObserved = rows.reduce((sum, a) => sum + a.observedCount, 0)
  if (rows.length < 2 || totalObserved === 0) return null

  let chiSquare = 0
  for (const row of rows) {
    const diff = row.observedCount - row.expectedCount
    chiSquare += (diff * diff) / row.expectedCount
  }

  const df = rows.length - 1
  const pValue = chiSquarePValue(chiSquare, df)
  return { chiSquare, df, pValue, healthy: pValue >= 0.01 }
}

/**
 * Welch's t-test on revenue per user.
 *
 * Revenue is heavily skewed — a yearly subscription is roughly ten times a
 * monthly one — so a lift of a few percent on a few hundred users is usually
 * noise. The variant payload carries the sum of squares so the variance is
 * computable without shipping every user's revenue.
 */
export function welchTTest (a, b) {
  const nA = a.count
  const nB = b.count
  if (nA < 2 || nB < 2) return { tStat: 0, pValue: 1 }

  const meanA = a.sum / nA
  const meanB = b.sum / nB
  // Sample variance from the sum of squares.
  const varA = Math.max(0, (a.sumOfSquares - nA * meanA * meanA) / (nA - 1))
  const varB = Math.max(0, (b.sumOfSquares - nB * meanB * meanB) / (nB - 1))

  const seSquared = varA / nA + varB / nB
  if (seSquared <= 0) return { tStat: 0, pValue: 1 }

  const tStat = (meanB - meanA) / Math.sqrt(seSquared)
  // Normal approximation to the t distribution; experiment arms are large
  // enough that the difference is immaterial.
  const pValue = 2 * (1 - normalCDF(Math.abs(tStat)))
  return { tStat, pValue }
}

/**
 * 95% confidence interval for a mean, from the same summary statistics.
 */
export function meanCI (summary) {
  const n = summary.count
  if (n < 2) return [0, 0]
  const mean = summary.sum / n
  const variance = Math.max(0, (summary.sumOfSquares - n * mean * mean) / (n - 1))
  const se = Math.sqrt(variance / n)
  return [mean - 1.96 * se, mean + 1.96 * se]
}

/**
 * 95% confidence interval for the *relative* lift between two proportions,
 * by the Katz log method: log of the rate ratio is far closer to normal than
 * the ratio itself, so the interval is built there and exponentiated back.
 *
 * The table already carries an interval on each rate, but two overlapping rate
 * intervals do not answer whether the lift interval clears zero — and that is
 * the question a reviewer actually came with. Returns [lower, upper] as
 * fractions (0.1 is +10%), or null when an arm has no conversions and the
 * ratio is undefined.
 */
export function relativeLiftCI (successesA, totalA, successesB, totalB) {
  if (!totalA || !totalB) return null
  if (successesA <= 0 || successesB <= 0) return null
  const pA = successesA / totalA
  const pB = successesB / totalB
  if (pA >= 1 || pB >= 1) return null
  const se = Math.sqrt((1 - pA) / successesA + (1 - pB) / successesB)
  const logRatio = Math.log(pB / pA)
  return [
    Math.exp(logRatio - 1.96 * se) - 1,
    Math.exp(logRatio + 1.96 * se) - 1
  ]
}

/**
 * 95% confidence interval for the relative lift between two means, from the
 * same summary statistics the Welch test uses. The interval is built on the
 * absolute difference and then divided by the control mean, which holds as
 * long as that mean is well clear of zero — true for per-user revenue on any
 * arm with purchases in it. Returns fractions, or null when it is not
 * estimable.
 */
export function meanLiftCI (control, variant) {
  const nA = control.count
  const nB = variant.count
  if (nA < 2 || nB < 2) return null
  const meanA = control.sum / nA
  const meanB = variant.sum / nB
  if (meanA <= 0) return null
  const varA = Math.max(0, (control.sumOfSquares - nA * meanA * meanA) / (nA - 1))
  const varB = Math.max(0, (variant.sumOfSquares - nB * meanB * meanB) / (nB - 1))
  const se = Math.sqrt(varA / nA + varB / nB)
  const diff = meanB - meanA
  return [(diff - 1.96 * se) / meanA, (diff + 1.96 * se) / meanA]
}

/**
 * Reduce a comparison to the three answers a reviewer can act on: this variant
 * beat control, it lost to control, or the test still cannot tell them apart.
 *
 * `threshold` is the multiplicity-adjusted level the page is reading at rather
 * than a bare 0.05 — a table running twenty comparisons turns up a p<0.05 by
 * chance about every other refresh, and "too close to call" is a far more
 * useful thing to show a decision maker than a green number that is noise.
 *
 * `blocked` says the comparison itself does not hold — a broken split, say.
 * There is no verdict to read in that case, whatever the arithmetic says, and
 * a green "Beats control" under a sample-ratio-mismatch banner is exactly how
 * a reviewer ships on a comparison that was never valid.
 */
export function readVerdict ({
  pValue, lift, threshold = 0.05, blocked = false
}) {
  if (blocked) {
    return {
      key: 'blocked',
      label: 'No verdict',
      badgeClass: 'text-bg-light',
      tone: 'muted'
    }
  }
  const hasTest = typeof pValue === 'number' && isFinite(pValue) &&
    typeof lift === 'number' && isFinite(lift)
  if (!hasTest) {
    return {
      key: 'unknown',
      label: 'Not enough data',
      badgeClass: 'text-bg-light',
      tone: 'muted'
    }
  }
  if (pValue < threshold) {
    return lift >= 0
      ? {
          key: 'win',
          label: 'Beats control',
          badgeClass: 'text-bg-success',
          tone: 'positive'
        }
      : {
          key: 'loss',
          label: 'Loses to control',
          badgeClass: 'text-bg-danger',
          tone: 'negative'
        }
  }
  return {
    key: 'inconclusive',
    label: 'Too close to call',
    badgeClass: 'text-bg-secondary',
    tone: 'muted'
  }
}

/**
 * Smallest relative lift detectable at this sample size, as a fraction of the
 * baseline rate. Without it "not significant" is unreadable: a reviewer can't
 * tell an underpowered test from a genuinely flat one.
 */
export function minimumDetectableEffect (
  baselineRate, nPerArm, alpha = 0.05, power = 0.8
) {
  if (!nPerArm || nPerArm <= 0) return null
  if (baselineRate <= 0 || baselineRate >= 1) return null

  const zAlpha = normalQuantile(1 - alpha / 2)
  const zBeta = normalQuantile(power)
  const absolute = (zAlpha + zBeta) *
    Math.sqrt(2 * baselineRate * (1 - baselineRate) / nPerArm)
  return absolute / baselineRate
}

/**
 * Significance label. There is deliberately no p<0.10 "marginally
 * significant" tier — it invited calling winners on noise.
 */
export function significanceLabel (pValue) {
  if (pValue < 0.01) {
    return { text: 'Significant at p<0.01', color: '#198754', symbol: '**' }
  }
  if (pValue < 0.05) {
    return { text: 'Significant at p<0.05', color: '#198754', symbol: '*' }
  }
  return { text: 'Not significant', color: '#6c757d', symbol: '' }
}

/**
 * Bonferroni-adjusted threshold. The page runs a test per metric per non-control
 * variant, and a reviewer refreshing daily is peeking on top of that, so the
 * naive 0.05 is optimistic.
 */
export function adjustedAlpha (comparisonCount, alpha = 0.05) {
  if (!comparisonCount || comparisonCount < 1) return alpha
  return alpha / comparisonCount
}
